/* Best sellers: spinning cans and quick add.
   A can video loads only once its card is on screen, so nothing downloads while the
   section is hidden. Safari and every iOS browser play VP9 without its transparency,
   so they get the HEVC file; the rest get VP9 WebM. Reduced motion keeps the still. */
{
  const appleVideo = /Apple/.test(navigator.vendor);
  const stillOnly = window.matchMedia('(prefers-reduced-motion: reduce)');
  const routesRoot = window.Shopify?.routes?.root ?? '/';

  const playCan = (box) => {
    const media = box.closest('.best-sellers-card__media');
    let video = box.querySelector('video');
    if (stillOnly.matches) {
      video?.pause();
      media.classList.remove('is-playing');
      return;
    }
    if (!video) {
      video = document.createElement('video');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      video.addEventListener('playing', () => media.classList.add('is-playing'));
      video.src = appleVideo ? box.dataset.hevc : box.dataset.webm;
      box.append(video);
    }
    video.play().catch(() => {});
  };

  const pauseCan = (box) => box.querySelector('video')?.pause();

  const watcher = new IntersectionObserver(
    (entries) => {
      for (const { target, isIntersecting } of entries) {
        if (isIntersecting) playCan(target);
        else pauseCan(target);
      }
    },
    { rootMargin: '200px 0px' }
  );

  const announce = (root, message) => {
    const status = root.querySelector('[data-best-sellers-status]');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  };

  const addToCart = async (root, button) => {
    if (button.getAttribute('aria-busy') === 'true') return;

    const { defaultText, variantId, productTitle } = button.dataset;
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding...';

    try {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const responseData = await response.json();
      if (!response.ok) throw responseData;

      const cart =
        typeof window.theme?.cart?.getCart === 'function'
          ? await window.theme.cart.getCart()
          : await fetch(`${routesRoot}cart.js`).then((cartResponse) => cartResponse.json());

      button.textContent = 'Added';
      announce(root, `${productTitle} added to cart`);

      const detail = {
        source: 'best-sellers-card',
        form: formData,
        product: responseData,
        response: responseData,
        cart,
        addToCartBtn: button,
      };
      document.dispatchEvent(new CustomEvent('product-form:submit:success', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('product-form:cart:update', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { bubbles: true, detail }));

      setTimeout(() => {
        button.textContent = defaultText;
      }, 1400);
    } catch (error) {
      console.error('Best sellers add to cart failed', error);
      button.textContent = 'Try again';
      announce(root, `${productTitle} could not be added. Try again.`);
      setTimeout(() => {
        button.textContent = defaultText;
      }, 1800);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  const init = (root) => {
    if (root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    for (const box of root.querySelectorAll('[data-best-sellers-can]')) watcher.observe(box);

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-best-sellers-add]');
      if (button && !button.disabled) addToCart(root, button);
    });
  };

  document.querySelectorAll('[data-best-sellers]').forEach(init);

  stillOnly.addEventListener('change', () => {
    for (const box of document.querySelectorAll('[data-best-sellers-can]')) {
      const { top, bottom } = box.getBoundingClientRect();
      if (bottom > 0 && top < window.innerHeight) playCan(box);
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-best-sellers]').forEach(init);
  });
}
